/**
 * Pixel Art Editor - Shopify Theme
 * Zero-server pixel art customization tool
 */
(function() {
  'use strict';

  // ─── 16 Classic Colors ─────────────────────────────────
  // Custom 16-color palette
  const COLORS = [
    '#ffffff', '#808080', '#3d3d3d', '#ffff00',
    '#7ed321', '#008000', '#0000ff', '#800080',
    '#e67e22', '#ffc0cb', '#f2b979', '#af601a',
    '#784212', '#62a7ff', '#2874a6', '#ff0000'
  ];

  const MAX_UNDO = 10;
  const DRAFT_KEY = 'cardPixelArtDrafts';
  const AUTO_SAVE_INTERVAL = 60000; // 60s

  // ── Cloudinary configuration (replace these two values after signing up) ──
  // 1. Register a free account at cloudinary.com
  // 2. Find your Cloud Name in the Dashboard
  // 3. Settings → Upload → Upload presets → create an unsigned preset
  const CLOUDINARY_CLOUD_NAME = 'dvutck1tg';               // ← Your Cloud Name
  const CLOUDINARY_UPLOAD_PRESET = 'missligarden';         // ← Unsigned Upload Preset name

  // ─── PixelArtEditor Class ──────────────────────────────
  class PixelArtEditor {
    constructor() {
      this.canvasCols = 45;        // grid columns
      this.canvasRows = 30;        // grid rows
      this.pixels = [];            // 2D: pixels[row][col] = colorIndex (active face)
      this.frontPixels = [];       // 2D: front face pixels
      this.backPixels = [];        // 2D: back face pixels
      this.activeFace = 0;         // 0 = front, 1 = back
      this.mask = null;            // 2D boolean: true = editable, false = excluded
      this.currentColor = 0;       // index into COLORS
      this.isDrawing = false;
      this.undoStack = [];
      this.currentDraftSlot = null;
      this.isLoggedIn = false;
      this.customerId = null;
      this.selectedProductId = null;
      this.selectedVariantId = null;
      this.autoSaveTimer = null;
      this.draftCount = 0;
      this.dragStartSlot = null;
      this.borderColor = 0;
      this.isDirty = false;
      this.isEraser = false;
      this.isEyedropper = false;
      this.currentAudioUrl = null;   // Audio URL from loaded pair (for cart)
      this.currentAudioName = null;  // Audio filename from loaded pair

      // Floating images (gallery import drag-to-position, refloat)
      this.floatingImages = [];        // Array of { pixels: 2D array, offsetRow, offsetCol }
      this.activeFloatingIdx = -1;     // Index of image being dragged/rotated
      this.isDraggingFloating = false; // true while dragging a floating image
      this.dragStart = null;           // { clientX, clientY, offsetRow, offsetCol, idx }
      this.lastTouchTime = 0;          // for touch double-tap detection
      this.lastTouchCell = null;       // for touch double-tap detection
      this.committedImages = [];       // Layer records for refloat (tracks each committed image separately)
      this.paintLayer = null;          // 2D array tracking manually painted pixels (topmost layer)
      // Per-face layer state caches (saved/restored on face switch)
      this.frontCommittedImages = [];
      this.backCommittedImages = [];
      this.frontPaintLayer = null;
      this.backPaintLayer = null;

      // DOM references — dual canvas (front + back displayed simultaneously)
      this.canvas = null;            // active face canvas element
      this.ctx = null;               // active face 2d context
      this.frontCanvasEl = null;
      this.backCanvasEl = null;
      this.frontCtx = null;
      this.backCtx = null;
      this.frontWrap = null;
      this.backWrap = null;
      this.frontOverlay = null;
      this.backOverlay = null;
      this.$ = (sel) => document.querySelector(sel);
      this.$$ = (sel) => document.querySelectorAll(sel);

      this.init();
    }

    init() {
      const el = this.$('.card-pixel-art-editor');
      if (!el) return;

      // Detect login state from Liquid-passed attributes (set in section)
      this.isLoggedIn = el.dataset.customerLoggedIn === 'true';
      this.customerId = el.dataset.customerId || null;

      // Editor mode: 'section' (standalone with own add-to-cart) | 'block' (embedded in product form)
      this.editorMode = el.dataset.editorMode || 'section';

      // Get DOM references — both canvases
      this.frontCanvasEl = this.$('#card-pixel-art-canvas-front');
      this.backCanvasEl = this.$('#card-pixel-art-canvas-back');
      if (!this.frontCanvasEl || !this.backCanvasEl) return;
      this.frontCtx = this.frontCanvasEl.getContext('2d');
      this.backCtx = this.backCanvasEl.getContext('2d');
      // Active face starts as front
      this.canvas = this.frontCanvasEl;
      this.ctx = this.frontCtx;

      this.initCanvas();
      this.initMask();
      this.initColors();
      this.initEvents();
      this.initAuthUI();

      // Default to slot 1 if no slot selected
      if (this.currentDraftSlot === null) {
        this.currentDraftSlot = 1;
      }

      this.loadSavedState();
      // Block mode: no standalone product selector — product comes from the product form
      if (this.editorMode !== 'block') this.loadProducts();

      // Gallery
      this.initGallery();

      // Render saved pairs
      this.renderSavedPairs();

      // Auto-save disabled — user saves manually via button
      // this.autoSaveTimer = setInterval(() => this.autoSave(), AUTO_SAVE_INTERVAL);
    }

    // ── Restricted Zone (Top face, slot 5) ─────────────────────

    isTopFace() {
      return this.currentDraftSlot === 5;
    }

    getRestrictedZone() {
      if (!this.isTopFace()) return null;
      const cols = this.canvasCols; const rows = this.canvasRows;
      const spanHeight = Math.floor(cols / 8);   // 16→2, 32→4
      const spanWidth  = Math.floor(cols * 3 / 4); // 16→12, 32→24
      const startRow = Math.floor((cols - spanHeight) / 2);
      const startCol = Math.floor((cols - spanWidth) / 2);
      return { startRow, startCol, spanHeight, spanWidth };
    }

    isInRestrictedZone(row, col) {
      const zone = this.getRestrictedZone();
      if (!zone) return false;
      return row >= zone.startRow && row < zone.startRow + zone.spanHeight &&
             col >= zone.startCol && col < zone.startCol + zone.spanWidth;
    }

    // ── Canvas ──────────────────────────────────────────────

    getCellSize() {
      // Compute cell size to fit canvas close to container width (~1080px)
      const maxDisplay = 1080;
      return Math.floor(Math.min(maxDisplay / this.canvasCols, maxDisplay / this.canvasRows));
    }

    // Setup wrap + overlay for a single canvas element
    _setupCanvasWrap(canvasEl) {
      const cellSize = this.getCellSize();
      const w = cellSize * this.canvasCols + 1;
      const h = cellSize * this.canvasRows + 1;
      canvasEl.width = w;
      canvasEl.height = h;
      canvasEl.style.width = w + 'px';
      canvasEl.style.height = h + 'px';

      const wrap = document.createElement('div');
      wrap.style.position = 'relative';
      wrap.style.display = 'inline-block';
      wrap.style.overflow = 'visible';
      canvasEl.parentNode.insertBefore(wrap, canvasEl);
      wrap.appendChild(canvasEl);

      const overlay = document.createElement('canvas');
      overlay.style.position = 'absolute';
      overlay.style.pointerEvents = 'none';
      overlay.style.zIndex = '10';
      overlay.style.maxWidth = 'none';
      overlay.style.maxHeight = 'none';
      overlay.style.aspectRatio = 'auto';
      overlay.style.border = 'none';
      overlay.style.boxShadow = 'none';
      wrap.appendChild(overlay);
      overlay.width = w * 3;
      overlay.height = h * 3;
      overlay.style.width = '300%';
      overlay.style.height = '300%';
      overlay.style.left = '-100%';
      overlay.style.top = '-100%';

      return { wrap, overlay };
    }

    initCanvas() {
      const cellSize = this.getCellSize();
      const w = cellSize * this.canvasCols + 1;
      const h = cellSize * this.canvasRows + 1;

      // Setup both canvases
      const front = this._setupCanvasWrap(this.frontCanvasEl);
      this.frontWrap = front.wrap;
      this.frontOverlay = front.overlay;
      const back = this._setupCanvasWrap(this.backCanvasEl);
      this.backWrap = back.wrap;
      this.backOverlay = back.overlay;

      // Set active references (front by default)
      this.canvasWrap = this.frontWrap;
      this.overlayCanvas = this.frontOverlay;
      this.overlayOffsetX = w;
      this.overlayOffsetY = h;

      // Init pixel arrays for both faces
      const initArr = () => {
        const arr = [];
        for (let r = 0; r < this.canvasRows; r++) {
          arr[r] = new Array(this.canvasCols).fill(null);
        }
        return arr;
      };
      if (this.frontPixels.length !== this.canvasRows) this.frontPixels = initArr();
      if (this.backPixels.length !== this.canvasRows) this.backPixels = initArr();
      // Always reference front pixels on init
      this.pixels = this.frontPixels;

      this.render();
      this.updateSizeDisplay();
    }

    switchFace(face) {
      if (this.activeFace === face) return;
      // Commit floating images before switching
      if (this.floatingImages.length > 0) this.commitFloatingImages();

      // Save current layer state to the old face
      if (this.activeFace === 0) {
        this.frontCommittedImages = this.committedImages;
        this.frontPaintLayer = this.paintLayer;
      } else {
        this.backCommittedImages = this.committedImages;
        this.backPaintLayer = this.paintLayer;
      }

      // Switch to target face
      this.activeFace = face;
      this.pixels = (face === 0) ? this.frontPixels : this.backPixels;
      this.committedImages = (face === 0) ? this.frontCommittedImages : this.backCommittedImages;
      this.paintLayer = (face === 0) ? this.frontPaintLayer : this.backPaintLayer;
      this.floatingImages = [];
      this.activeFloatingIdx = -1;
      this.undoStack = [];

      // Swap active canvas references
      if (face === 0) {
        this.canvas = this.frontCanvasEl;
        this.ctx = this.frontCtx;
        this.canvasWrap = this.frontWrap;
        this.overlayCanvas = this.frontOverlay;
      } else {
        this.canvas = this.backCanvasEl;
        this.ctx = this.backCtx;
        this.canvasWrap = this.backWrap;
        this.overlayCanvas = this.backOverlay;
      }

      this.render();
      this.updateActiveFaceUI();
    }

    updateActiveFaceUI() {
      const panels = document.querySelectorAll('.card-pixel-art-editor__face-panel');
      panels.forEach(p => {
        p.classList.toggle('is-active', parseInt(p.dataset.face) === this.activeFace);
      });
    }

    initMask() {
      // Load mask from reference PNG alpha channel
      const el = this.$('.card-pixel-art-editor');
      const maskUrl = el ? el.dataset.maskUrl : '';
      if (!maskUrl) return;

      const maskImg = new Image();
      maskImg.crossOrigin = 'anonymous';
      maskImg.onload = () => {
        const tmp = document.createElement('canvas');
        tmp.width = maskImg.width;
        tmp.height = maskImg.height;
        const ctx = tmp.getContext('2d');
        ctx.drawImage(maskImg, 0, 0);
        const data = ctx.getImageData(0, 0, maskImg.width, maskImg.height).data;

        const cellW = maskImg.width / this.canvasCols;
        const cellH = maskImg.height / this.canvasRows;

        this.mask = [];
        for (let r = 0; r < this.canvasRows; r++) {
          this.mask[r] = [];
          for (let c = 0; c < this.canvasCols; c++) {
            // Sample center pixel of each cell
            const px = Math.floor(c * cellW + cellW / 2);
            const py = Math.floor(r * cellH + cellH / 2);
            const idx = (py * maskImg.width + px) * 4;
            this.mask[r][c] = data[idx + 3] >= 128; // alpha >= 128 = editable
          }
        }
        this.render(); // Re-render with mask applied
        this.renderSavedPairs(); // Re-render thumbnails with mask shape
      };
      maskImg.src = maskUrl;
    }

    // Render grid + pixels for a single face onto a given context
    renderFaceContent(ctx, pixels, canvasEl) {
      const cellSize = this.getCellSize();
      const cols = this.canvasCols;
      const rows = this.canvasRows;

      // Clear canvas to transparent
      ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);

      // Fill editable cells with background color (creates the shape)
      const bgColor = COLORS[this.borderColor] || '#ffffff';
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (this.mask && !this.mask[r][c]) continue;
          ctx.fillStyle = bgColor;
          ctx.fillRect(c * cellSize + 1, r * cellSize + 1, cellSize, cellSize);
        }
      }

      // Draw pixel colors on top of background
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (this.mask && !this.mask[r][c]) continue;
          const colorIdx = pixels[r] && pixels[r][c] !== undefined && pixels[r][c] !== null
            ? pixels[r][c] : null;
          if (colorIdx !== null) {
            ctx.fillStyle = COLORS[colorIdx] || COLORS[1];
            ctx.fillRect(c * cellSize + 2, r * cellSize + 2, cellSize - 1, cellSize - 1);
          }
        }
      }

      // Draw grid lines (black) only between adjacent editable cells
      if (this.mask) {
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        for (let r = 1; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            if (this.mask[r - 1][c] && this.mask[r][c]) {
              const x1 = c * cellSize + 2;
              const x2 = (c + 1) * cellSize + 1;
              const y = r * cellSize;
              ctx.moveTo(x1, y);
              ctx.lineTo(x2, y);
            }
          }
        }
        for (let c = 1; c < cols; c++) {
          for (let r = 0; r < rows; r++) {
            if (this.mask[r][c - 1] && this.mask[r][c]) {
              const x = c * cellSize;
              const y1 = r * cellSize + 2;
              const y2 = (r + 1) * cellSize + 1;
              ctx.moveTo(x, y1);
              ctx.lineTo(x, y2);
            }
          }
        }
        ctx.stroke();
      }

      // Draw mask boundary
      if (this.mask) {
        ctx.strokeStyle = '#555';
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            if (!this.mask[r][c]) continue;
            const x = c * cellSize;
            const y = r * cellSize;
            if (r === 0 || !this.mask[r - 1][c]) {
              ctx.moveTo(x, y); ctx.lineTo(x + cellSize, y);
            }
            if (r === rows - 1 || !this.mask[r + 1][c]) {
              ctx.moveTo(x, y + cellSize); ctx.lineTo(x + cellSize, y + cellSize);
            }
            if (c === 0 || !this.mask[r][c - 1]) {
              ctx.moveTo(x, y); ctx.lineTo(x, y + cellSize);
            }
            if (c === cols - 1 || !this.mask[r][c + 1]) {
              ctx.moveTo(x + cellSize, y); ctx.lineTo(x + cellSize, y + cellSize);
            }
          }
        }
        ctx.stroke();
      }

      // Draw restricted zone overlay for top face (slot 5)
      const zone = this.getRestrictedZone();
      if (zone) {
        const x = zone.startCol * cellSize + 1;
        const y = zone.startRow * cellSize + 1;
        const w = zone.spanWidth * cellSize - 1;
        const h = zone.spanHeight * cellSize - 1;
        ctx.fillStyle = '#000000';
        ctx.fillRect(x, y, w, h);
      }
    }

    render() {
      // Render active face (grid + pixels)
      this.renderFaceContent(this.ctx, this.pixels, this.canvas);

      // Render inactive face (grid + pixels, no overlay)
      const inactivePixels = this.activeFace === 0 ? this.backPixels : this.frontPixels;
      const inactiveCtx = this.activeFace === 0 ? this.backCtx : this.frontCtx;
      const inactiveCanvas = this.activeFace === 0 ? this.backCanvasEl : this.frontCanvasEl;
      this.renderFaceContent(inactiveCtx, inactivePixels, inactiveCanvas);

      // Toggle restricted zone hint
      const hint = this.$('#card-pixel-art-restricted-hint');
      if (hint) {
        hint.style.display = this.isTopFace() ? 'block' : 'none';
      }

      // Render floating images overlay (active face only)
      if (this.floatingImages.length > 0) {
        this.syncOverlaySize();
        this.renderFloatingImages();
      } else if (this.overlayCanvas) {
        const octx = this.overlayCanvas.getContext('2d');
        octx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
      }

      // Clear inactive face overlay
      const inactiveOverlay = this.activeFace === 0 ? this.backOverlay : this.frontOverlay;
      if (inactiveOverlay) {
        const ioctx = inactiveOverlay.getContext('2d');
        ioctx.clearRect(0, 0, inactiveOverlay.width, inactiveOverlay.height);
      }

      // Update button states
      const rotateBtn = document.getElementById('card-pixel-art-rotate');
      if (rotateBtn) {
        const hasFloating = this.floatingImages.length > 0;
        rotateBtn.disabled = !hasFloating;
        rotateBtn.style.opacity = hasFloating ? '' : '0.4';
        rotateBtn.style.cursor = hasFloating ? '' : 'not-allowed';
      }
      const mirrorBtn = document.getElementById('card-pixel-art-mirror');
      if (mirrorBtn) {
        const hasFloating = this.floatingImages.length > 0;
        mirrorBtn.disabled = !hasFloating;
        mirrorBtn.style.opacity = hasFloating ? '' : '0.4';
        mirrorBtn.style.cursor = hasFloating ? '' : 'not-allowed';
      }
      const deleteBtn = document.getElementById('card-pixel-art-delete');
      if (deleteBtn) {
        const hasFloating = this.floatingImages.length > 0;
        deleteBtn.disabled = !hasFloating;
        deleteBtn.style.opacity = hasFloating ? '' : '0.4';
        deleteBtn.style.cursor = hasFloating ? '' : 'not-allowed';
      }
      const refloatBtn = document.getElementById('card-pixel-art-refloat');
      if (refloatBtn) {
        const inDragMode = this.floatingImages.length > 0;
        refloatBtn.textContent = inDragMode ? '📍 Exit Move' : '📍 Move';
        refloatBtn.style.backgroundColor = inDragMode ? 'rgba(255, 165, 0, 0.3)' : '';
      }
    }

    // Sync overlay canvas CSS size to match main canvas (accounts for border + scaling)
    syncOverlaySize() {
      if (!this.overlayCanvas || !this.canvasWrap) return;
      const cssW = this.canvas.clientWidth || this.canvas.offsetWidth;
      const cssH = this.canvas.clientHeight || this.canvas.offsetHeight;
      if (cssW === 0 || cssH === 0) return;
      const cs = window.getComputedStyle(this.canvas);
      const bl = parseFloat(cs.borderLeftWidth) || 0;
      const bt = parseFloat(cs.borderTopWidth) || 0;
      this.overlayCanvas.style.width = (cssW * 3) + 'px';
      this.overlayCanvas.style.height = (cssH * 3) + 'px';
      this.overlayCanvas.style.left = (bl - cssW) + 'px';
      this.overlayCanvas.style.top = (bt - cssH) + 'px';
    }

    // ── Floating Images (gallery drag-to-position) ──────────

    renderFloatingImages() {
      const ctx = this.overlayCanvas.getContext('2d');
      ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
      const cellSize = this.getCellSize();
      const ox = this.overlayOffsetX;
      const oy = this.overlayOffsetY;

      for (let idx = 0; idx < this.floatingImages.length; idx++) {
        const fi = this.floatingImages[idx];
        const isActive = idx === this.activeFloatingIdx;

        // Draw all floating pixels (no bounds check — overlay extends beyond main canvas)
        for (let r = 0; r < fi.pixels.length; r++) {
          for (let c = 0; c < fi.pixels[r].length; c++) {
            if (fi.pixels[r][c] === null) continue;
            const x = (fi.offsetCol + c) * cellSize + 2 + ox;
            const y = (fi.offsetRow + r) * cellSize + 2 + oy;
            ctx.fillStyle = COLORS[fi.pixels[r][c]] || COLORS[1];
            ctx.fillRect(x, y, cellSize - 1, cellSize - 1);
          }
        }

        // Draw dashed border (active = orange, inactive = blue)
        const bx = fi.offsetCol * cellSize + ox;
        const by = fi.offsetRow * cellSize + oy;
        const bw = fi.pixels[0].length * cellSize;
        const bh = fi.pixels.length * cellSize;
        ctx.strokeStyle = isActive ? 'rgba(255, 165, 0, 0.9)' : 'rgba(100, 200, 255, 0.7)';
        ctx.lineWidth = isActive ? 2 : 1;
        ctx.setLineDash(isActive ? [6, 3] : [3, 3]);
        ctx.strokeRect(bx, by, bw, bh);
        ctx.setLineDash([]);
      }
    }

    findFloatingImageAt(row, col) {
      for (let i = this.floatingImages.length - 1; i >= 0; i--) {
        const fi = this.floatingImages[i];
        const imgRow = row - fi.offsetRow;
        const imgCol = col - fi.offsetCol;
        if (imgRow < 0 || imgRow >= fi.pixels.length) continue;
        if (imgCol < 0 || imgCol >= fi.pixels[0].length) continue;
        if (fi.pixels[imgRow][imgCol] !== null) return i;
      }
      return -1;
    }

    commitFloatingImages() {
      if (this.floatingImages.length === 0) return;
      const cols = this.canvasCols;
      const rows = this.canvasRows;

      this.saveSnapshot();

      for (const fi of this.floatingImages) {
        // Record layer info for potential refloat (enables separating overlapping images)
        this.committedImages.push({
          pixels: fi.pixels.map(row => [...row]),
          offsetRow: fi.offsetRow,
          offsetCol: fi.offsetCol
        });
        // Commit pixels to canvas
        for (let r = 0; r < fi.pixels.length; r++) {
          for (let c = 0; c < fi.pixels[r].length; c++) {
            if (fi.pixels[r][c] === null) continue;
            const tRow = fi.offsetRow + r;
            const tCol = fi.offsetCol + c;
            if (tRow >= 0 && tRow < rows && tCol >= 0 && tCol < cols) {
              if (this.mask && !this.mask[tRow][tCol]) continue;
              if (!this.isInRestrictedZone(tRow, tCol)) {
                this.pixels[tRow][tCol] = fi.pixels[r][c];
              }
            }
          }
        }
      }

      this.floatingImages = [];
      this.activeFloatingIdx = -1;
      this.isDirty = true;
      this.render();
      this.showMessage('Pattern fixed. You can continue editing pixels.', 'success');
    }

    cancelFloatingImages() {
      if (this.floatingImages.length === 0) return;
      this.floatingImages = [];
      this.activeFloatingIdx = -1;
      this.render();
      this.showMessage('Gallery import cancelled.', 'info');
    }

    // Extract manually painted pixels as a separate floating image (topmost layer)
    extractPaintLayer() {
      if (!this.paintLayer) return null;
      const rows = this.paintLayer.length;
      const cols = this.paintLayer[0].length;
      let minR = rows, maxR = -1, minC = cols, maxC = -1;
      let hasPaint = false;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (this.paintLayer[r][c] !== null) {
            hasPaint = true;
            if (r < minR) minR = r;
            if (r > maxR) maxR = r;
            if (c < minC) minC = c;
            if (c > maxC) maxC = c;
          }
        }
      }
      if (!hasPaint) return null;
      const extRows = maxR - minR + 1;
      const extCols = maxC - minC + 1;
      const extracted = Array.from({ length: extRows }, () => new Array(extCols).fill(null));
      for (let r = minR; r <= maxR; r++) {
        for (let c = minC; c <= maxC; c++) {
          extracted[r - minR][c - minC] = this.paintLayer[r][c];
        }
      }
      return { pixels: extracted, offsetRow: minR, offsetCol: minC };
    }

    refloatCanvas() {
      if (this.floatingImages.length > 0) {
        this.showMessage('Floating patterns already exist. Please fix or cancel them first.', 'info');
        return;
      }
      const cols = this.canvasCols;
      const rows = this.canvasRows;

      // ── Layer-aware path: use committed image records for precise separation ──
      // This handles overlapping images correctly (flood fill would merge them)
      if (this.committedImages.length > 0) {
        this.saveSnapshot();
        // Clear canvas
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            if (!this.mask || this.mask[r][c]) {
              this.pixels[r][c] = null;
            }
          }
        }
        // Recreate floating images from layer records
        this.floatingImages = this.committedImages.map(ci => ({
          pixels: ci.pixels.map(row => [...row]),
          offsetRow: ci.offsetRow,
          offsetCol: ci.offsetCol
        }));
        this.committedImages = [];

        // Extract paint layer as topmost floating image
        const paintComponent = this.extractPaintLayer();
        if (paintComponent) {
          this.floatingImages.push(paintComponent);
        }
        this.paintLayer = null;

        this.activeFloatingIdx = this.floatingImages.length > 0 ? 0 : -1;
        this.isDirty = true;
        this.render();
        this.showMessage(this.floatingImages.length > 1
          ? `Separated ${this.floatingImages.length} patterns (including overlaps). Click to select, drag to move, double-click to fix all.`
          : 'Drag mode activated. Drag patterns to move, double-click to fix.', 'info');
        return;
      }

      // ── Fallback: flood fill (8-connectivity) for manually painted pixels ──
      const visited = Array.from({ length: rows }, () => new Array(cols).fill(false));
      const components = [];

      for (let sr = 0; sr < rows; sr++) {
        for (let sc = 0; sc < cols; sc++) {
          if (visited[sr][sc]) continue;
          if (this.pixels[sr][sc] === null) continue;
          if (this.mask && !this.mask[sr][sc]) continue;

          // BFS flood fill
          const queue = [[sr, sc]];
          visited[sr][sc] = true;
          const pixels = [];
          let minR = sr, maxR = sr, minC = sc, maxC = sc;

          while (queue.length > 0) {
            const [r, c] = queue.shift();
            pixels.push([r, c, this.pixels[r][c]]);
            if (r < minR) minR = r; if (r > maxR) maxR = r;
            if (c < minC) minC = c; if (c > maxC) maxC = c;

            // 8-connectivity neighbors
            for (let dr = -1; dr <= 1; dr++) {
              for (let dc = -1; dc <= 1; dc++) {
                if (dr === 0 && dc === 0) continue;
                const nr = r + dr, nc = c + dc;
                if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
                if (visited[nr][nc]) continue;
                if (this.pixels[nr][nc] === null) continue;
                if (this.mask && !this.mask[nr][nc]) continue;
                visited[nr][nc] = true;
                queue.push([nr, nc]);
              }
            }
          }

          // Build trimmed pixel array for this component
          const extRows = maxR - minR + 1;
          const extCols = maxC - minC + 1;
          const extracted = Array.from({ length: extRows }, () => new Array(extCols).fill(null));
          for (const [r, c, val] of pixels) {
            extracted[r - minR][c - minC] = val;
          }
          components.push({ pixels: extracted, offsetRow: minR, offsetCol: minC });
        }
      }

      if (components.length === 0) {
        this.showMessage('Canvas is empty, nothing to reposition.', 'info');
        return;
      }

      // Clear the canvas
      this.saveSnapshot();
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (!this.mask || this.mask[r][c]) {
            this.pixels[r][c] = null;
          }
        }
      }
      // Create floating images
      this.paintLayer = null;
      this.floatingImages = components;
      this.activeFloatingIdx = components.length > 0 ? 0 : -1;
      this.isDirty = true;
      this.render();
      this.showMessage(components.length > 1
        ? `Separated ${components.length} patterns. Click to select, drag to move, double-click to fix all.`
        : 'Drag mode activated. Drag patterns to move, double-click to fix.', 'info');
    }

    // ── Drawing ─────────────────────────────────────────────

    getCellFromPos(clientX, clientY) {
      const rect = this.canvas.getBoundingClientRect();
      const scaleX = this.canvas.width / rect.width;
      const scaleY = this.canvas.height / rect.height;
      const x = (clientX - rect.left) * scaleX;
      const y = (clientY - rect.top) * scaleY;
      const cellSize = this.getCellSize();
      const col = Math.floor(x / cellSize);
      const row = Math.floor(y / cellSize);
      if (row >= 0 && row < this.canvasRows && col >= 0 && col < this.canvasCols) {
        return { row, col };
      }
      return null;
    }

    paintPixel(row, col, recordUndo = true) {
      if (!this.pixels[row]) return;
      // Block drawing on masked-out cells
      if (this.mask && !this.mask[row][col]) return;
      // Block drawing in restricted zone for top face
      if (this.isInRestrictedZone(row, col)) return;
      const prev = this.pixels[row][col];
      // Eraser mode: set pixel to null (empty, shows canvas bg = border color)
      const newColor = this.isEraser ? null : this.currentColor;
      if (prev === newColor) return;

      // Track manually painted pixel in paint layer (topmost layer, independent from committed images)
      if (!this.paintLayer) {
        this.paintLayer = Array.from({ length: this.canvasRows }, () => new Array(this.canvasCols).fill(null));
      }
      if (this.paintLayer[row]) {
        this.paintLayer[row][col] = newColor;
      }

      this.pixels[row][col] = newColor;
      this.isDirty = true;
      this.render();
    }

    // Save pixel snapshot for stroke-level undo (includes floating image state)
    saveSnapshot() {
      const snapshot = {
        pixels: this.pixels.map(row => [...row]),
        floatingImages: this.floatingImages.map(fi => ({
          pixels: fi.pixels.map(row => [...row]),
          offsetRow: fi.offsetRow,
          offsetCol: fi.offsetCol
        })),
        activeFloatingIdx: this.activeFloatingIdx,
        committedImages: this.committedImages.map(ci => ({
          pixels: ci.pixels.map(row => [...row]),
          offsetRow: ci.offsetRow,
          offsetCol: ci.offsetCol
        })),
        paintLayer: this.paintLayer ? this.paintLayer.map(row => [...row]) : null
      };
      this.undoStack.push(snapshot);
      if (this.undoStack.length > MAX_UNDO) this.undoStack.shift();
    }

    paintLine(fromRow, fromCol, toRow, toCol) {
      // Simple line interpolation for continuous drawing
      const dx = Math.abs(toCol - fromCol);
      const dy = Math.abs(toRow - fromRow);
      const steps = Math.max(dx, dy);
      if (steps === 0) {
        this.paintPixel(toRow, toCol, false);
        return;
      }
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const r = Math.round(fromRow + (toRow - fromRow) * t);
        const c = Math.round(fromCol + (toCol - fromCol) * t);
        this.paintPixel(r, c, false);
      }
    }

    // ── Events ──────────────────────────────────────────────

    // ── Gallery ──────────────────────────────────────────────

    initGallery() {
      const grid = this.$('#card-pixel-art-gallery-grid');
      if (!grid || grid._galleryBound) return;
      grid._galleryBound = true;

      grid.addEventListener('click', (e) => {
        const item = e.target.closest('.card-pixel-art-editor__gallery-item');
        if (!item) return;
        const img = item.querySelector('img');
        if (!img) return;
        // Gallery import now creates a draggable floating image, no need to warn about overwriting
        this.loadGalleryImage(img.src);
      });
    }

    // ── Auth UI ───────────────────────────────────────────────

    initAuthUI() {
      const loginBtns = document.querySelectorAll('.pixel-auth-login-btn');
      const saveBtns = document.querySelectorAll('.pixel-auth-save-btn');
      const hintTexts = document.querySelectorAll('.pixel-auth-hint-text');

      if (this.isLoggedIn) {
        // Logged in: show save buttons, hide login buttons
        loginBtns.forEach(el => el.style.display = 'none');
        saveBtns.forEach(el => el.style.display = 'inline-flex');
        hintTexts.forEach(el => el.textContent = 'Drafts auto-synced to cloud');
      } else {
        // Not logged in: show login buttons, hide save buttons
        loginBtns.forEach(el => el.style.display = 'inline-flex');
        saveBtns.forEach(el => el.style.display = 'none');
        hintTexts.forEach(el => el.textContent = 'Log in to save drafts and continue later');
      }
    }

    quantizeGalleryImage(img) {
      const cols = this.canvasCols; const rows = this.canvasRows;
      const superScale = 4;
      const bigW = cols * superScale;  // 180
      const bigH = rows * superScale;  // 120

      // Step 1: Draw to larger canvas for super-sampling (no white fill to preserve PNG transparency)
      const bigCanvas = document.createElement('canvas');
      bigCanvas.width = bigW;
      bigCanvas.height = bigH;
      const bigCtx = bigCanvas.getContext('2d');
      bigCtx.imageSmoothingEnabled = true;
      bigCtx.imageSmoothingQuality = 'high';

      const scale = Math.min(bigW / img.width, bigH / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      const x = (bigW - w) / 2;
      const y = (bigH - h) / 2;
      bigCtx.drawImage(img, x, y, w, h);

      const bigData = bigCtx.getImageData(0, 0, bigW, bigH).data;

      // Step 2: Majority vote — quantize each super-sampled pixel, then pick most frequent color per block
      // Transparent pixels (alpha < 128) are counted separately and mapped to null
      const result = [];
      for (let r = 0; r < rows; r++) {
        result[r] = [];
        for (let c = 0; c < cols; c++) {
          const colorCounts = new Array(COLORS.length).fill(0);
          let transparentCount = 0;

          for (let sr = 0; sr < superScale; sr++) {
            for (let sc = 0; sc < superScale; sc++) {
              const px = c * superScale + sc;
              const py = r * superScale + sr;
              const idx = (py * bigW + px) * 4;
              const pA = bigData[idx + 3];

              // Transparent pixel → count separately
              if (pA < 128) {
                transparentCount++;
                continue;
              }

              const pR = bigData[idx];
              const pG = bigData[idx + 1];
              const pB = bigData[idx + 2];

              // Near-gray snap
              const maxDiff = Math.max(Math.abs(pR - pG), Math.abs(pG - pB), Math.abs(pR - pB));
              let fR = pR, fG = pG, fB = pB;
              if (maxDiff < 15) {
                const gray = Math.round((pR + pG + pB) / 3);
                fR = gray; fG = gray; fB = gray;
              }

              const colorIdx = this.findNearestColor(fR, fG, fB);
              colorCounts[colorIdx]++;
            }
          }

          // Pick the most frequent color (majority vote)
          let bestColor = 0, bestCount = 0;
          for (let i = 0; i < colorCounts.length; i++) {
            if (colorCounts[i] > bestCount) {
              bestCount = colorCounts[i];
              bestColor = i;
            }
          }

          result[r][c] = (transparentCount > bestCount) ? null : bestColor;
        }
      }
      return result;
    }

    // Trim transparent edges to get the bounding box of actual pixels
    trimTransparent(pixels) {
      let minR = pixels.length, maxR = -1, minC = pixels[0].length, maxC = -1;
      for (let r = 0; r < pixels.length; r++) {
        for (let c = 0; c < pixels[r].length; c++) {
          if (pixels[r][c] !== null) {
            if (r < minR) minR = r;
            if (r > maxR) maxR = r;
            if (c < minC) minC = c;
            if (c > maxC) maxC = c;
          }
        }
      }
      if (maxR < 0) return pixels; // all transparent
      const trimmed = [];
      for (let r = minR; r <= maxR; r++) {
        trimmed.push(pixels[r].slice(minC, maxC + 1));
      }
      return trimmed;
    }

    loadGalleryImage(src) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // Quantize image to pixel array
        const rawPixels = this.quantizeGalleryImage(img);
        // Trim transparent borders so the image is tight
        const trimmed = this.trimTransparent(rawPixels);

        // Save state before import for undo
        this.saveSnapshot();

        // Add new floating image to the array
        const newFi = {
          pixels: trimmed,
          offsetRow: 0,
          offsetCol: 0
        };
        this.floatingImages.push(newFi);
        this.activeFloatingIdx = this.floatingImages.length - 1;
        this.centerFloatingImage(newFi);

        this.isDirty = true;
        this.render();
        this.showMessage('Gallery template loaded. Drag to move, double-click to fix.', 'info');
      };
      img.onerror = () => {
        this.showMessage('Image failed to load. Please try again.', 'error');
      };
      img.src = src;
    }

    centerFloatingImage(fi) {
      if (!fi) return;
      const imgRows = fi.pixels.length;
      const imgCols = fi.pixels[0].length;
      fi.offsetRow = Math.floor((this.canvasRows - imgRows) / 2);
      fi.offsetCol = Math.floor((this.canvasCols - imgCols) / 2);
    }

    findNearestColor(r, g, b) {
      let minDist = Infinity;
      let bestIdx = 0;
      for (let i = 0; i < COLORS.length; i++) {
        const hex = COLORS[i];
        const cr = parseInt(hex.slice(1, 3), 16);
        const cg = parseInt(hex.slice(3, 5), 16);
        const cb = parseInt(hex.slice(5, 7), 16);
        const dist = (r - cr) ** 2 + (g - cg) ** 2 + (b - cb) ** 2;
        if (dist < minDist) {
          minDist = dist;
          bestIdx = i;
        }
      }
      return bestIdx;
    }

    initEvents() {
      // Attach events to both canvases — clicking inactive canvas switches to it
      const canvases = [this.frontCanvasEl, this.backCanvasEl];
      canvases.forEach(canvas => {
        const face = canvas === this.frontCanvasEl ? 0 : 1;

        canvas.addEventListener('mousedown', (e) => {
          if (face !== this.activeFace) this.switchFace(face);
          this.handlePointerDown(e);
        });
        canvas.addEventListener('mousemove', (e) => {
          if (canvas !== this.canvas) return; // only active canvas
          this.handlePointerMove(e);
        });
        canvas.addEventListener('mouseup', () => this.handlePointerUp());
        canvas.addEventListener('mouseleave', () => this.handlePointerUp());

        canvas.addEventListener('dblclick', (e) => {
          if (this.floatingImages.length > 0) {
            e.preventDefault();
            this.commitFloatingImages();
          }
        });

        canvas.addEventListener('touchstart', (e) => {
          if (face !== this.activeFace) this.switchFace(face);
          this.handleTouchStart(e);
        }, { passive: false });
        canvas.addEventListener('touchmove', (e) => {
          if (canvas !== this.canvas) return;
          this.handleTouchMove(e);
        }, { passive: false });
        canvas.addEventListener('touchend', () => this.handlePointerUp());
      });

      // Escape key to cancel floating images
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.floatingImages.length > 0) {
          this.cancelFloatingImages();
        }
      });

      // Warn before leaving/closing page if canvas has content (production only)
      const self = this;
      const isDevServer = location.hostname === '127.0.0.1' || location.hostname === 'localhost';
      if (!isDevServer) {
        window.addEventListener('beforeunload', function(e) {
          if (self.hasPixels()) {
            e.preventDefault();
            e.returnValue = 'You have unsaved changes. Leaving the page will lose your work.';
            return e.returnValue;
          }
        });
      }

      // Size buttons — handled by document-level delegation (prevents duplicate handlers)

      // Action buttons — use data-action for delegation (prevents duplicate handlers)
      // Handled by document-level listener at bottom

      // Product selector
      this.$('#card-pixel-art-product')?.addEventListener('change', (e) => {
        this.selectedProductId = e.target.value;
        if (this.selectedProductId) {
          this.loadVariants(this.selectedProductId);
        } else {
          this.$('#card-pixel-art-variant').style.display = 'none';
          this.updateCartButton();
        }
      });
      this.$('#card-pixel-art-variant')?.addEventListener('change', (e) => {
        this.selectedVariantId = e.target.value;
        this.updateCartButton();
      });

      // Add to cart
      this.$('#card-pixel-art-add-to-cart')?.addEventListener('click', () => this.addToCart());
    }

    handlePointerDown(e) {
      const cell = this.getCellFromPos(e.clientX, e.clientY);

      // If floating images exist, handle drag or commit
      if (this.floatingImages.length > 0) {
        if (cell) {
          const idx = this.findFloatingImageAt(cell.row, cell.col);
          if (idx >= 0) {
            // Clicked on a floating image — select it and start dragging
            this.activeFloatingIdx = idx;
            this.saveSnapshot();
            this.isDraggingFloating = true;
            const fi = this.floatingImages[idx];
            this.dragStart = {
              clientX: e.clientX,
              clientY: e.clientY,
              offsetRow: fi.offsetRow,
              offsetCol: fi.offsetCol,
              idx: idx
            };
            this.canvas.style.cursor = 'grabbing';
            this.render();
            return;
          }
        }
        // Clicked outside all floating images — commit all
        this.commitFloatingImages();
        return;
      }

      if (!cell) return;

      // Eyedropper mode: pick color from pixel
      if (this.isEyedropper) {
        this.pickColor(cell.row, cell.col);
        return;
      }

      this.saveSnapshot(); // Save state before stroke begins
      this.isDrawing = true;
      this.lastPaintCell = cell;
      this.paintPixel(cell.row, cell.col, true);
    }

    handlePointerMove(e) {
      // Dragging floating image
      if (this.isDraggingFloating && this.activeFloatingIdx >= 0) {
        const fi = this.floatingImages[this.dragStart.idx];
        if (!fi) return;
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;
        const dx = (e.clientX - this.dragStart.clientX) * scaleX;
        const dy = (e.clientY - this.dragStart.clientY) * scaleY;
        const cellSize = this.getCellSize();
        fi.offsetCol = this.dragStart.offsetCol + Math.round(dx / cellSize);
        fi.offsetRow = this.dragStart.offsetRow + Math.round(dy / cellSize);
        this.render();
        return;
      }

      const cell = this.getCellFromPos(e.clientX, e.clientY);

      // Update cursor when hovering over floating images
      if (this.floatingImages.length > 0) {
        const idx = cell ? this.findFloatingImageAt(cell.row, cell.col) : -1;
        this.canvas.style.cursor = idx >= 0 ? 'grab' : 'default';
      }

      if (!cell) return;

      // Update coordinate display
      this.$('#card-pixel-art-coord').textContent = `${cell.row}, ${cell.col}`;

      if (this.isDrawing && this.lastPaintCell) {
        this.paintLine(this.lastPaintCell.row, this.lastPaintCell.col, cell.row, cell.col);
      }
      this.lastPaintCell = cell;
    }

    handlePointerUp() {
      this.isDrawing = false;
      this.lastPaintCell = null;
      if (this.isDraggingFloating) {
        this.isDraggingFloating = false;
        this.dragStart = null;
        this.canvas.style.cursor = this.floatingImages.length > 0 ? 'grab' : 'default';
      }
    }

    handleTouchStart(e) {
      e.preventDefault();
      const touch = e.touches[0];

      // Double-tap detection for touch devices
      const cell = this.getCellFromPos(touch.clientX, touch.clientY);
      const now = Date.now();
      if (this.floatingImages.length > 0 && cell && this.lastTouchCell &&
          now - this.lastTouchTime < 350 &&
          Math.abs(cell.row - this.lastTouchCell.row) <= 2 &&
          Math.abs(cell.col - this.lastTouchCell.col) <= 2) {
        // Double-tap: commit floating images
        this.commitFloatingImages();
        this.lastTouchTime = 0;
        this.lastTouchCell = null;
        return;
      }
      this.lastTouchTime = now;
      this.lastTouchCell = cell;

      this.handlePointerDown(touch);
    }

    handleTouchMove(e) {
      e.preventDefault();
      const touch = e.touches[0];
      this.handlePointerMove(touch);
    }

    // ── Color Palette ───────────────────────────────────────

    initColors() {
      const container = this.$('#card-pixel-art-colors');
      if (!container) return;

      // Clear and dynamically create swatches (original working pattern)
      // All styles set via JS to work even without external CSS
      container.innerHTML = '';
      container.style.display = 'flex';
      container.style.flexWrap = 'wrap';
      container.style.gap = '4px';

      COLORS.forEach((color, idx) => {
        const swatch = document.createElement('div');
        swatch.className = 'color-swatch' + (idx === this.currentColor ? ' active' : '');
        swatch.style.backgroundColor = color;
        swatch.style.width = '42px';
        swatch.style.height = '42px';
        swatch.style.border = '2px solid transparent';
        swatch.style.borderRadius = '6px';
        swatch.style.cursor = 'pointer';
        swatch.style.boxSizing = 'border-box';
        swatch.style.flexShrink = '0';
        swatch.style.transition = 'transform 0.1s, border-color 0.1s';
        // White and light colors get a subtle border
        if (idx === 0 || idx === 9) {
          swatch.style.border = '2px solid #ccc';
        }
        swatch.addEventListener('click', () => this.selectColor(idx));
        // Prevent Dawn theme's div:empty { display:none } from hiding the swatch
        swatch.appendChild(document.createTextNode('\u200B'));
        container.appendChild(swatch);
      });
    }

    selectColor(idx) {
      this.currentColor = idx;
      this.isEraser = false;
      this.isEyedropper = false;
      // Deactivate eraser and eyedropper buttons
      const eraserBtn = document.getElementById('card-pixel-art-eraser');
      if (eraserBtn) eraserBtn.classList.remove('active');
      const eyeBtn = document.getElementById('card-pixel-art-eyedropper');
      if (eyeBtn) eyeBtn.classList.remove('active');
      // Activate selected palette swatch
      document.querySelectorAll('#card-pixel-art-colors .color-swatch').forEach((s) => s.classList.remove('active'));
      const swatches = document.querySelectorAll('#card-pixel-art-colors .color-swatch');
      if (swatches[idx]) swatches[idx].classList.add('active');
    }

    // ── Border Color Palette ──────────────────────────────────

    initBorderColors() {
      const container = this.$('#card-pixel-art-border-colors');
      if (!container) return;

      container.innerHTML = '';

      COLORS.forEach((color, idx) => {
        const swatch = document.createElement('div');
        swatch.className = 'border-swatch' + (idx === this.borderColor ? ' active' : '');
        swatch.style.backgroundColor = color;
        if (idx === 0 || idx === 9) swatch.style.border = '2px solid #ccc';
        swatch.appendChild(document.createTextNode('\u200B'));
        swatch.addEventListener('click', () => this.selectBorderColor(idx));
        container.appendChild(swatch);
      });
    }

    selectBorderColor(idx) {
      this.borderColor = idx;
      // If eraser is active, update its color too
      if (this.isEraser) {
        this.currentColor = idx;
      }
      document.querySelectorAll('#card-pixel-art-border-colors .border-swatch').forEach((s) => s.classList.remove('active'));
      const swatches = document.querySelectorAll('#card-pixel-art-border-colors .border-swatch');
      if (swatches[idx]) swatches[idx].classList.add('active');
      // Re-render canvas and cube preview with new border color
      this.render();
      
    }

    // ── Tools ───────────────────────────────────────────────

    undo() {
      if (this.undoStack.length === 0) return;
      const snapshot = this.undoStack.pop();
      this.pixels = snapshot.pixels.map(row => [...row]);
      this.floatingImages = (snapshot.floatingImages || []).map(fi => ({
        pixels: fi.pixels.map(row => [...row]),
        offsetRow: fi.offsetRow,
        offsetCol: fi.offsetCol
      }));
      this.activeFloatingIdx = snapshot.activeFloatingIdx || -1;
      this.committedImages = (snapshot.committedImages || []).map(ci => ({
        pixels: ci.pixels.map(row => [...row]),
        offsetRow: ci.offsetRow,
        offsetCol: ci.offsetCol
      }));
      this.paintLayer = snapshot.paintLayer ? snapshot.paintLayer.map(row => [...row]) : null;
      this.isDraggingFloating = false;
      this.render();
    }

    clear() {
      if (!this.hasPixels()) return;
      if (!confirm('Are you sure you want to clear the canvas?')) return;
      for (let r = 0; r < this.canvasRows; r++) {
        for (let c = 0; c < this.canvasCols; c++) {
          this.pixels[r][c] = null;
        }
      }
      this.floatingImages = [];
      this.activeFloatingIdx = -1;
      this.committedImages = [];
      this.paintLayer = null;
      // Sync per-face cache
      if (this.activeFace === 0) { this.frontCommittedImages = []; this.frontPaintLayer = null; }
      else { this.backCommittedImages = []; this.backPaintLayer = null; }
      this.isDraggingFloating = false;
      this.undoStack = [];
      this.isDirty = true;
      this.render();
    }

    rotateCanvas() {
      if (!this.hasPixels()) return;
      this.saveSnapshot();
      this.committedImages = []; // Rotation breaks layer tracking
      this.paintLayer = null;
      // Sync per-face cache
      if (this.activeFace === 0) { this.frontCommittedImages = []; this.frontPaintLayer = null; }
      else { this.backCommittedImages = []; this.backPaintLayer = null; }
      const cols = this.canvasCols; const rows = this.canvasRows;
      const newPixels = [];
      for (let r = 0; r < this.canvasRows; r++) {
        newPixels[r] = [];
        for (let c = 0; c < this.canvasCols; c++) {
          // Rotate 90° clockwise: new[r][c] = old[size-1-c][r]
          newPixels[r][c] = this.pixels[this.canvasCols - 1 - c][r];
        }
      }
      this.pixels = newPixels;
      this.isDirty = true;
      this.render();
      
    }

    rotateFloatingImage() {
      if (this.activeFloatingIdx < 0 || !this.floatingImages[this.activeFloatingIdx]) return;
      this.saveSnapshot();
      const fi = this.floatingImages[this.activeFloatingIdx];
      const oldPixels = fi.pixels;
      const R = oldPixels.length;
      const C = oldPixels[0].length;
      // Rotate 90° clockwise: new dimensions are C rows × R cols
      const newPixels = [];
      for (let r = 0; r < C; r++) {
        newPixels[r] = [];
        for (let c = 0; c < R; c++) {
          newPixels[r][c] = oldPixels[R - 1 - c][r];
        }
      }
      // Keep center position stable
      const oldCenterRow = fi.offsetRow + R / 2;
      const oldCenterCol = fi.offsetCol + C / 2;
      fi.pixels = newPixels;
      fi.offsetRow = Math.round(oldCenterRow - C / 2);
      fi.offsetCol = Math.round(oldCenterCol - R / 2);
      this.isDirty = true;
      this.render();
    }

    mirrorFloatingImage() {
      if (this.activeFloatingIdx < 0 || !this.floatingImages[this.activeFloatingIdx]) return;
      this.saveSnapshot();
      const fi = this.floatingImages[this.activeFloatingIdx];
      const oldPixels = fi.pixels;
      const R = oldPixels.length;
      const C = oldPixels[0].length;
      // Horizontal mirror: new[r][c] = old[r][C-1-c]
      const newPixels = [];
      for (let r = 0; r < R; r++) {
        newPixels[r] = [];
        for (let c = 0; c < C; c++) {
          newPixels[r][c] = oldPixels[r][C - 1 - c];
        }
      }
      fi.pixels = newPixels;
      this.isDirty = true;
      this.render();
    }

    deleteFloatingImage() {
      if (this.activeFloatingIdx < 0 || !this.floatingImages[this.activeFloatingIdx]) return;
      this.saveSnapshot();
      this.floatingImages.splice(this.activeFloatingIdx, 1);
      if (this.floatingImages.length > 0) {
        this.activeFloatingIdx = Math.min(this.activeFloatingIdx, this.floatingImages.length - 1);
      } else {
        this.activeFloatingIdx = -1;
      }
      this.isDraggingFloating = false;
      this.isDirty = true;
      this.render();
      this.showMessage(this.floatingImages.length > 0
        ? 'Selected image deleted. Continue editing remaining images.'
        : 'Last image deleted.', 'info');
    }

    toggleEraser() {
      this.isEraser = !this.isEraser;
      this.isEyedropper = false; // Mutually exclusive
      // Deactivate eyedropper button
      const eyeBtn = document.getElementById('card-pixel-art-eyedropper');
      if (eyeBtn) eyeBtn.classList.remove('active');
      // Reset canvas cursor
      this.canvas.style.cursor = 'crosshair';

      if (this.isEraser) {
        this.currentColor = this.borderColor;
        document.querySelectorAll('#card-pixel-art-colors .color-swatch').forEach(s => s.classList.remove('active'));
      } else {
        this.currentColor = 0;
        document.querySelectorAll('#card-pixel-art-colors .color-swatch').forEach((s, i) => {
          s.classList.toggle('active', i === 0);
        });
      }
      const eraserBtn = document.getElementById('card-pixel-art-eraser');
      if (eraserBtn) eraserBtn.classList.toggle('active', this.isEraser);
    }

    toggleEyedropper() {
      this.isEyedropper = !this.isEyedropper;
      this.isEraser = false; // Reset eraser to prevent paintPixel using border color
      // Deactivate eraser button
      const eraserBtn = document.getElementById('card-pixel-art-eraser');
      if (eraserBtn) eraserBtn.classList.remove('active');

      if (this.isEyedropper) {
        this.canvas.style.cursor = 'crosshair';
        document.querySelectorAll('#card-pixel-art-colors .color-swatch').forEach(s => s.classList.remove('active'));
      } else {
        this.canvas.style.cursor = 'crosshair';
      }
      // Toggle eyedropper button active state
      const eyeBtn = document.getElementById('card-pixel-art-eyedropper');
      if (eyeBtn) eyeBtn.classList.toggle('active', this.isEyedropper);
    }

    pickColor(row, col) {
      if (!this.pixels[row]) return;
      const colorIdx = this.pixels[row][col];
      if (colorIdx === null || colorIdx === undefined) return; // Empty pixel, skip

      // Set picked color as current brush
      this.currentColor = colorIdx;
      this.isEyedropper = false;
      this.isEraser = false; // Critical: prevent paintPixel from using border color
      this.canvas.style.cursor = 'crosshair';

      // Deactivate eyedropper button
      const eyeBtn = document.getElementById('card-pixel-art-eyedropper');
      if (eyeBtn) eyeBtn.classList.remove('active');

      // Activate the picked color in palette
      document.querySelectorAll('#card-pixel-art-colors .color-swatch').forEach((s, i) => {
        s.classList.toggle('active', i === colorIdx);
      });
    }

    resize(size) {
      
      this.pixels = [];
      this.undoStack = [];
      this.initCanvas();
      this.updateSizeDisplay();
    }

    hasPixels() {
      for (let r = 0; r < this.canvasRows; r++) {
        for (let c = 0; c < this.canvasCols; c++) {
          if (this.pixels[r] && this.pixels[r][c] !== null && this.pixels[r][c] !== undefined) return true;
        }
      }
      return false;
    }

    updateSizeDisplay() {
      const el = this.$('#card-pixel-art-size-display');
      if (el) el.textContent = `${this.canvasCols}×${this.canvasRows}`;
    }

    // ── Canvas to PNG ───────────────────────────────────────

    toDataURL() {
      return this.canvas.toDataURL('image/png');
    }

    // ── Block mode: data exposure for product-form.js ───────
    // These methods let the parent product form collect the artwork/audio
    // and append them as line item properties when the buyer adds to cart.

    /** Returns a Promise that resolves to a PNG Blob of the current canvas. */
    toBlob() {
      return new Promise((resolve) => {
        if (!this.canvas) {
          resolve(null);
          return;
        }
        // Commit floating images first so the PNG includes them (must happen before hasPixels check)
        if (this.floatingImages.length > 0) this.commitFloatingImages();
        if (!this.hasPixels()) {
          resolve(null);
          return;
        }
        this.canvas.toBlob((blob) => resolve(blob), 'image/png');
      });
    }

    /** Compressed pixel data string: rows of comma-separated color indices joined by '|'. */
    getPixelString() {
      if (!this.pixels || !this.hasPixels()) return '';
      return this.pixels.map(row => row.join(',')).join('|');
    }

    /** Canvas size label, e.g. "45×30". */
    getSizeLabel() {
      return `${this.canvasCols}×${this.canvasRows}`;
    }

    /**
     * Collects all editor data for the product form.
     * Returns a Promise resolving to:
     *   { pngBlob: Blob|null, pixelStr: string, size: string,
     *     audioUrl: string|null, audioName: string|null, hasContent: boolean }
     */
    async getEditorData() {
      // Sync current face state before exporting
      if (this.activeFace === 0) {
        this.frontPixels = this.pixels;
      } else {
        this.backPixels = this.pixels;
      }
      const pngBlob = await this.toBlob();
      const pixelStr = this.getPixelString();
      return {
        pngBlob: pngBlob,
        pixelStr: pixelStr,
        size: this.getSizeLabel(),
        audioUrl: this.currentAudioUrl || null,
        audioName: this.currentAudioName || null,
        hasContent: !!(pngBlob && pixelStr)
      };
    }

    // ── Draft Management ────────────────────────────────────

    getDrafts() {
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch { return []; }
    }

    saveDraftsToLocal(drafts) {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(drafts));
      } catch { /* Ignore quota errors */ }
    }

    saveDraft() {
      // Commit floating images before saving
      if (this.floatingImages.length > 0) this.commitFloatingImages();
      const dataURL = this.toDataURL();
      const drafts = this.getDrafts();
      const now = new Date().toISOString();

      const entry = {
        canvasCols: this.canvasCols, canvasRows: this.canvasRows,
        pixels: this.pixels.map(row => [...row]),
        committedImages: this.committedImages.map(ci => ({
          pixels: ci.pixels.map(row => [...row]),
          offsetRow: ci.offsetRow,
          offsetCol: ci.offsetCol
        })),
        paintLayer: this.paintLayer ? this.paintLayer.map(row => [...row]) : null,
        preview: dataURL,
        updatedAt: now
      };

      if (this.currentDraftSlot !== null) {
        // Update existing slot (works for both logged-in and guest)
        entry.slot = this.currentDraftSlot;
        const idx = drafts.findIndex((d) => d.slot === this.currentDraftSlot);
        if (idx >= 0) {
          drafts[idx] = { ...drafts[idx], ...entry, slot: this.currentDraftSlot };
        } else {
          drafts.push(entry);
        }
      } else {
        // No current slot: find first empty slot (1-5)
        for (let s = 1; s <= 5; s++) {
          if (!drafts.find((d) => d.slot === s)) {
            entry.slot = s;
            this.currentDraftSlot = s;
            break;
          }
        }

        // If no empty slot and under 5, use length+1
        if (this.currentDraftSlot === null && drafts.length < 5) {
          this.currentDraftSlot = drafts.length + 1;
          entry.slot = this.currentDraftSlot;
        }

        if (this.currentDraftSlot !== null) {
          const idx = drafts.findIndex((d) => d.slot === this.currentDraftSlot);
          if (idx >= 0) {
            drafts[idx] = entry;
          } else {
            drafts.push(entry);
          }
        } else {
          // Max 5 reached, overwrite last
          drafts[4] = entry;
          this.currentDraftSlot = 5;
        }
      }

      this.saveDraftsToLocal(drafts);

      // If logged in, also save to server
      if (this.isLoggedIn) {
        this.saveDraftsToServer(drafts);
      }

      this.showMessage('Draft saved!', 'success');
      this.isDirty = false;
      this.renderDraftSlots();
      
    }

    loadDraft(slot) {
      const drafts = this.getDrafts();
      const draft = drafts.find((d) => d.slot === slot);
      if (!draft) return;

      this.currentDraftSlot = slot;
      // Restore layer tracking from draft
      this.committedImages = (draft.committedImages || []).map(ci => ({
        pixels: ci.pixels.map(row => [...row]),
        offsetRow: ci.offsetRow,
        offsetCol: ci.offsetCol
      }));
      this.paintLayer = draft.paintLayer ? draft.paintLayer.map(row => [...row]) : null;
      this.floatingImages = [];
      this.activeFloatingIdx = -1;
      this.canvasCols = draft.canvasCols || 45; this.canvasRows = draft.canvasRows || 30;
      this.pixels = draft.pixels.map(row => [...row]);
      this.initCanvas();

      this.render();
      this.showMessage('Draft loaded.', 'success');
    }

    selectSlot(slot) {
      const drafts = this.getDrafts();
      const draft = drafts.find((d) => d.slot === slot);

      // Warn only if: dirty + different slot + target slot has a draft
      if (this.isDirty && this.currentDraftSlot !== slot && draft) {
        if (!confirm('The canvas has unsaved changes. Switching slots will load that slot\'s draft and lose unsaved changes. Continue?')) return;
      }

      this.currentDraftSlot = slot;

      if (draft) {
        // Load draft's canvas size and pixels
        this.canvasCols = draft.canvasCols || 45; this.canvasRows = draft.canvasRows || 30;
        this.pixels = draft.pixels.map(row => [...row]);
        this.isDirty = false;
        this.initCanvas();
        this.render();
      } else {
        // Empty slot: don't clear canvas, just switch slot
        this.render();
      }

      this.renderDraftSlots();
      
    }

    deleteDraft(slot) {
      let drafts = this.getDrafts();
      drafts = drafts.filter((d) => d.slot !== slot);
      this.saveDraftsToLocal(drafts);

      if (this.currentDraftSlot === slot) {
        // Stay on current slot, just clear the canvas
        for (let r = 0; r < this.canvasRows; r++) {
          for (let c = 0; c < this.canvasCols; c++) {
            this.pixels[r][c] = null;
          }
        }
        this.undoStack = [];
        this.render();
      }

      if (this.isLoggedIn) {
        this.saveDraftsToServer(drafts);
      }

      this.renderDraftSlots();
      
    }

    autoSave() {
      if (!this.hasPixels()) return;
      // Only auto-save if there are meaningful changes (non-empty)
      this.saveDraft();
    }

    loadSavedState() {
      const self = this;

      const afterLoad = () => {
        self.renderDraftSlots();
        // Always load slot 1 by default
        self.selectSlot(1);
      };

      if (this.isLoggedIn) {
        // First try to load from server
        this.loadDraftsFromServer().then((serverDrafts) => {
          if (serverDrafts && serverDrafts.length > 0) {
            self.saveDraftsToLocal(serverDrafts);
          }
          afterLoad();
        }).catch(() => {
          afterLoad();
        });
      } else {
        afterLoad();
      }
    }

    renderDraftSlots() {
      const container = this.$('#card-pixel-art-draft-slots');
      const panel = this.$('#card-pixel-art-drafts');
      const authSection = this.$('#card-pixel-art-auth');
      if (!container) return;

      const drafts = this.getDrafts();
      this.draftCount = drafts.length;
      const slotNames = ['Front', 'Left', 'Back', 'Right', 'Top'];

      // Toggle auth section based on login (both desktop and mobile)
      const authSectionMobile = this.$('#card-pixel-art-auth-mobile');
      if (authSection) {
        authSection.style.display = this.isLoggedIn ? 'none' : 'block';
      }
      if (authSectionMobile) {
        authSectionMobile.style.display = this.isLoggedIn ? 'none' : 'block';
      }
      // Drafts panel always visible
      if (panel) {
        panel.style.display = 'block';
      }

      container.innerHTML = '';

      const self = this;

      // Show 5 slots
      for (let s = 1; s <= 5; s++) {
        const draft = drafts.find((d) => d.slot === s);
        const name = slotNames[s - 1];
        const isSelected = this.currentDraftSlot === s;

        // Sub-container: wraps slot + upload button
        const wrap = document.createElement('div');
        wrap.className = 'card-pixel-art-editor__slot-wrap';
        if (isSelected) wrap.classList.add('selected');

        const slotEl = document.createElement('div');
        slotEl.className = 'card-pixel-art-editor__draft-slot';
        slotEl.dataset.slot = s;
        if (isSelected) slotEl.classList.add('selected');

        if (draft) {
          // Existing draft — draggable
          slotEl.draggable = true;
          slotEl.title = 'Click to switch · Drag to reorder';
          slotEl.addEventListener('dragstart', function(e) {
            self.dragStartSlot = s;
            this.classList.add('drag-source');
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', s);
          });
          slotEl.addEventListener('dragend', function() {
            this.classList.remove('drag-source');
            self.dragStartSlot = null;
            container.querySelectorAll('.drag-over').forEach(el => el.classList.remove('drag-over'));
          });

          const thumb = document.createElement('img');
          thumb.className = 'draft-thumb';
          thumb.src = draft.preview || '';
          thumb.alt = name;
          thumb.draggable = false;

          const nameEl = document.createElement('div');
          nameEl.className = 'draft-name';
          nameEl.textContent = name;

          const delBtn = document.createElement('button');
          delBtn.className = 'draft-delete';
          delBtn.textContent = '✕';
          delBtn.title = 'Delete draft';
          delBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (confirm('Are you sure you want to delete this draft?')) this.deleteDraft(s);
          });

          slotEl.appendChild(thumb);
          slotEl.appendChild(nameEl);
          slotEl.appendChild(delBtn);

          // Click to select and load
          slotEl.addEventListener('click', () => this.selectSlot(s));
        } else {
          // Empty slot
          slotEl.classList.add('draft-slot-empty');

          const nameEl = document.createElement('div');
          nameEl.className = 'draft-name';
          nameEl.textContent = name;

          const emptyHint = document.createElement('div');
          emptyHint.className = 'draft-empty-hint';
          emptyHint.textContent = '(Empty)';

          slotEl.appendChild(nameEl);
          slotEl.appendChild(emptyHint);

          // Click to select empty slot
          slotEl.addEventListener('click', () => this.selectSlot(s));
        }

        // Common drag-over/drop on slot element
        slotEl.addEventListener('dragover', function(e) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (self.dragStartSlot && self.dragStartSlot !== parseInt(this.dataset.slot)) {
            this.classList.add('drag-over');
          }
        });
        slotEl.addEventListener('dragleave', function() {
          this.classList.remove('drag-over');
        });
        slotEl.addEventListener('drop', function(e) {
          e.preventDefault();
          this.classList.remove('drag-over');
          const fromSlot = self.dragStartSlot;
          const toSlot = parseInt(this.dataset.slot);
          if (fromSlot && fromSlot !== toSlot) {
            self.swapDrafts(fromSlot, toSlot);
          }
        });

        wrap.appendChild(slotEl);

        // Upload button inside selected slot's wrap
        if (isSelected) {
          const uploadBtn = document.createElement('button');
          uploadBtn.type = 'button';
          uploadBtn.className = 'card-pixel-art-btn card-pixel-art-btn--upload';
          uploadBtn.id = 'card-pixel-art-upload';
          uploadBtn.title = 'Upload draft';
          uploadBtn.textContent = '💾 Save';
          wrap.appendChild(uploadBtn);
        }

        container.appendChild(wrap);
      }
    }

    swapDrafts(fromSlot, toSlot) {
      let drafts = this.getDrafts();
      const fromDraft = drafts.find((d) => d.slot === fromSlot);
      const toDraft = drafts.find((d) => d.slot === toSlot);

      if (fromDraft) fromDraft.slot = toSlot;
      if (toDraft) toDraft.slot = fromSlot;

      this.saveDraftsToLocal(drafts);
      if (this.isLoggedIn) {
        this.saveDraftsToServer(drafts);
      }
      this.renderDraftSlots();
      this.showMessage('Slots swapped.', 'success');
    }

    async loadDraftsFromServer() {
      // This requires Storefront API with customer access token
      // Returns the drafts array or null
      return null; // Placeholder - advanced integration
    }

    async saveDraftsToServer(drafts) {
      // Placeholder for Storefront API integration
      // Requires customer access token + metafield setup
    }

    // ── Product Loading ─────────────────────────────────────

    loadProducts() {
      const select = this.$('#card-pixel-art-product');
      if (!select) return;

      // Show loading
      select.innerHTML = '<option value="">Loading products...</option>';

      fetch('/collections/all/products.json?limit=50&sort_by=title')
        .then((res) => {
          if (!res.ok) throw new Error('Failed to load products');
          return res.json();
        })
        .then((data) => {
          select.innerHTML = '<option value="">-- Select product --</option>';
          (data.products || []).forEach((product) => {
            const opt = document.createElement('option');
            opt.value = product.id;
            opt.textContent = product.title;
            select.appendChild(opt);
          });
        })
        .catch((err) => {
          console.error('Failed to load products:', err);
          select.innerHTML = '<option value="">Failed to load. Please refresh the page.</option>';
        });
    }

    loadVariants(productId) {
      const variantSelect = this.$('#card-pixel-art-variant');
      if (!variantSelect) return;

      variantSelect.innerHTML = '<option value="">Loading variants...</option>';
      variantSelect.style.display = 'block';

      fetch(`/products/${productId}/variants.json`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to load variants');
          return res.json();
        })
        .then((data) => {
          variantSelect.innerHTML = '<option value="">-- Select variant --</option>';
          (data.variants || []).forEach((v) => {
            const opt = document.createElement('option');
            opt.value = v.id;
            opt.textContent = `${v.title}${v.price ? ' - ¥' + (v.price / 100).toFixed(2) : ''}`;
            if (v.available === false) opt.disabled = true;
            variantSelect.appendChild(opt);
          });
          this.updateCartButton();
        })
        .catch(() => {
          variantSelect.innerHTML = '<option value="">Failed to load</option>';
        });
    }

    // ── Cart Integration ────────────────────────────────────

    updateCartButton() {
      const btn = this.$('#card-pixel-art-add-to-cart');
      if (!btn) return;

      const hasProduct = this.selectedProductId && this.selectedVariantId;
      const hasArt = this.hasPixels();
      btn.disabled = !(hasProduct && hasArt);

      if (!hasProduct) {
        btn.textContent = '🛒 Select product and variant';
      } else if (!hasArt) {
        btn.textContent = '🛒 Draw pixel art first';
      } else {
        btn.textContent = '🛒 Add to Cart';
      }
    }

    addToCart() {
      const btn = this.$('#card-pixel-art-add-to-cart');
      const msgEl = this.$('#card-pixel-art-cart-message');
      if (!btn || btn.disabled) return;
      if (!this.selectedVariantId) return;

      // Commit floating images before adding to cart
      if (this.floatingImages.length > 0) this.commitFloatingImages();

      // Get pixel data as properties
      const pngData = this.toDataURL();

      // Compress pixel data: rows of comma-separated indices
      const pixelStr = this.pixels.map(row => row.join(',')).join('|');

      btn.disabled = true;
      btn.textContent = '⏳ Adding...';
      if (msgEl) msgEl.textContent = '';
      msgEl.className = 'card-pixel-art-editor__cart-message';

      const formData = {
        items: [{
          id: this.selectedVariantId,
          quantity: 1,
          properties: {
            _pixel_art: pixelStr,
            _pixel_art_size: `${this.canvasCols}×${this.canvasRows}`,
            _pixel_art_preview: pngData,
            ...(this.currentAudioUrl ? { _audio_url: this.currentAudioUrl, _audio_name: this.currentAudioName || 'audio' } : {})
          }
        }]
      };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(formData)
      })
        .then((res) => {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then(() => {
          if (msgEl) {
            msgEl.textContent = '✅ Added to cart! Redirecting...';
            msgEl.className = 'card-pixel-art-editor__cart-message success';
          }
          // Show cart drawer or redirect to cart
          setTimeout(() => {
            window.location.href = '/cart';
          }, 800);
        })
        .catch((err) => {
          console.error('Add to cart failed:', err);
          if (msgEl) {
            msgEl.textContent = '❌ Failed to add to cart. Please try again.';
            msgEl.className = 'card-pixel-art-editor__cart-message error';
          }
          btn.disabled = false;
          btn.textContent = '🛒 Add to Cart';
        });
    }

    // ── UI Helpers ──────────────────────────────────────────

    showMessage(text, type) {
      const el = this.$('#card-pixel-art-cart-message');
      if (!el) return;
      el.textContent = text;
      el.className = 'card-pixel-art-editor__cart-message ' + (type || '');
      setTimeout(() => {
        if (el.textContent === text) el.textContent = '';
      }, 3000);
    }

    // ── Saved Pairs ──────────────────────────────────────────

    savePair() {
      // Commit floating images before saving
      if (this.floatingImages.length > 0) this.commitFloatingImages();
      // Sync current state to active face
      if (this.activeFace === 0) {
        this.frontPixels = this.pixels;
        this.frontCommittedImages = this.committedImages;
        this.frontPaintLayer = this.paintLayer;
      } else {
        this.backPixels = this.pixels;
        this.backCommittedImages = this.committedImages;
        this.backPaintLayer = this.paintLayer;
      }
      // Only save if at least one face has pixels
      const hasFront = this.frontPixels && this.frontPixels.some(row => row.some(p => p !== null));
      const hasBack = this.backPixels && this.backPixels.some(row => row.some(p => p !== null));
      if (!hasFront && !hasBack) return;

      const pair = {
        frontPixels: this.frontPixels ? this.frontPixels.map(row => [...row]) : [],
        backPixels: this.backPixels ? this.backPixels.map(row => [...row]) : [],
        frontCommittedImages: (this.frontCommittedImages || []).map(ci => ({
          pixels: ci.pixels.map(row => [...row]),
          offsetRow: ci.offsetRow,
          offsetCol: ci.offsetCol
        })),
        backCommittedImages: (this.backCommittedImages || []).map(ci => ({
          pixels: ci.pixels.map(row => [...row]),
          offsetRow: ci.offsetRow,
          offsetCol: ci.offsetCol
        })),
        frontPaintLayer: this.frontPaintLayer ? this.frontPaintLayer.map(row => [...row]) : null,
        backPaintLayer: this.backPaintLayer ? this.backPaintLayer.map(row => [...row]) : null,
        canvasCols: this.canvasCols,
        canvasRows: this.canvasRows,
        borderColor: this.borderColor,
        savedAt: new Date().toISOString()
      };

      const pairs = this.getSavedPairs();
      pairs.unshift(pair);
      localStorage.setItem('cardPixelArtSavedPairs', JSON.stringify(pairs));
      this.renderSavedPairs();
      this.showMessage('Front/back patterns saved!', 'success');
      this.isDirty = false;
    }

    getSavedPairs() {
      try {
        const raw = localStorage.getItem('cardPixelArtSavedPairs');
        return raw ? JSON.parse(raw) : [];
      } catch { return []; }
    }

    loadSavedPair(index) {
      // Warn if there are unsaved changes
      if (this.isDirty) {
        if (!confirm('The canvas has unsaved changes. Loading a saved work will lose them. Continue?')) return;
      }
      const pairs = this.getSavedPairs();
      const pair = pairs[index];
      if (!pair) return;
      // Restore both faces' pixels and layer state
      this.frontPixels = (pair.frontPixels || []).map(row => [...row]);
      this.backPixels = (pair.backPixels || []).map(row => [...row]);
      this.frontCommittedImages = (pair.frontCommittedImages || []).map(ci => ({
        pixels: ci.pixels.map(row => [...row]),
        offsetRow: ci.offsetRow,
        offsetCol: ci.offsetCol
      }));
      this.backCommittedImages = (pair.backCommittedImages || []).map(ci => ({
        pixels: ci.pixels.map(row => [...row]),
        offsetRow: ci.offsetRow,
        offsetCol: ci.offsetCol
      }));
      this.frontPaintLayer = pair.frontPaintLayer ? pair.frontPaintLayer.map(row => [...row]) : null;
      this.backPaintLayer = pair.backPaintLayer ? pair.backPaintLayer.map(row => [...row]) : null;
      // Set active face state
      this.pixels = this.activeFace === 0 ? this.frontPixels : this.backPixels;
      this.committedImages = this.activeFace === 0 ? this.frontCommittedImages : this.backCommittedImages;
      this.paintLayer = this.activeFace === 0 ? this.frontPaintLayer : this.backPaintLayer;
      this.floatingImages = [];
      this.activeFloatingIdx = -1;
      this.undoStack = [];
      // Remember audio URL for cart
      this.currentAudioUrl = pair.audioUrl || null;
      this.currentAudioName = pair.audioName || null;
      this.isDirty = false;
      this.render();
      this.showMessage('Saved work loaded.', 'success');
    }

    uploadAudioForPair(index) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'audio/*';
      input.style.display = 'none';
      input.addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        // Validate audio type
        if (!file.type.startsWith('audio/')) {
          this.showMessage('Please select an audio file.', 'error');
          return;
        }
        // Limit file size to 20MB
        if (file.size > 20 * 1024 * 1024) {
          this.showMessage('Audio file must not exceed 20MB.', 'error');
          return;
        }

        // Show uploading status on button
        const btn = document.querySelector(`[data-audio-idx="${index}"]`);
        if (btn) {
          btn.innerHTML = '<span class="audio-spinner">◯</span> Uploading';
          btn.disabled = true;
          btn.style.cursor = 'not-allowed';
          btn.style.opacity = '0.7';
        }
        const pairs = this.getSavedPairs();
        if (!pairs[index]) return;

        // Upload to Cloudinary
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
        formData.append('resource_type', 'auto');

        try {
          const response = await fetch(
            `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
            { method: 'POST', body: formData }
          );
          if (!response.ok) throw new Error('Upload failed');
          const data = await response.json();

          // Store URL (not file data) in localStorage — very small
          const pairs = this.getSavedPairs();
          if (!pairs[index]) return;
          pairs[index].audioUrl = data.secure_url;
          pairs[index].audioName = file.name;
          pairs[index].audioSize = file.size;
          localStorage.setItem('cardPixelArtSavedPairs', JSON.stringify(pairs));
          this.renderSavedPairs();
          this.showMessage('Audio uploaded successfully!', 'success');
        } catch (err) {
          console.error('Cloudinary upload error:', err);
          this.renderSavedPairs(); // Restore button state on failure
          alert('Audio upload failed. Please try again.');
        }
      });
      document.body.appendChild(input);
      input.click();
      document.body.removeChild(input);
    }

    deleteSavedPair(index) {
      const pairs = this.getSavedPairs();
      pairs.splice(index, 1);
      localStorage.setItem('cardPixelArtSavedPairs', JSON.stringify(pairs));
      this.renderSavedPairs();
    }

    renderSavedPairs() {
      const grid = this.$('#card-pixel-art-saved-grid');
      if (!grid) return;
      grid.innerHTML = '';

      const pairs = this.getSavedPairs();
      if (pairs.length === 0) {
        grid.innerHTML = '<div style="text-align:center;color:rgba(var(--color-foreground),0.3);padding:2rem;">No saved works yet</div>';
        return;
      }

      const self = this;
      pairs.forEach((pair, idx) => {
        const item = document.createElement('div');
        item.className = 'card-pixel-art-editor__saved-item';
        // Background: green if audio uploaded, red if not
        item.style.backgroundColor = pair.audioUrl ? '#d4edda' : '#f8d7da';

        // Create preview canvases for each face
        const createFacePreview = (pixels, label) => {
          const wrap = document.createElement('div');
          const labelEl = document.createElement('div');
          labelEl.className = 'saved-face-label';
          labelEl.textContent = label;
          wrap.appendChild(labelEl);

          const preview = document.createElement('canvas');
          const scale = 3;
          preview.width = pair.canvasCols * scale;
          preview.height = pair.canvasRows * scale;
          preview.style.width = (pair.canvasCols * scale) + 'px';
          preview.style.height = (pair.canvasRows * scale) + 'px';
          const pctx = preview.getContext('2d');
          pctx.imageSmoothingEnabled = false;
          // Clear to transparent, then fill only masked cells (creates card shape)
          pctx.clearRect(0, 0, preview.width, preview.height);
          const bgColor = COLORS[pair.borderColor || 0];
          for (let r = 0; r < pair.canvasRows; r++) {
            for (let c = 0; c < pair.canvasCols; c++) {
              if (self.mask && !self.mask[r][c]) continue;
              // Fill cell background
              pctx.fillStyle = bgColor;
              pctx.fillRect(c * scale, r * scale, scale, scale);
              // Draw pixel on top
              if (pixels[r] && pixels[r][c] !== null) {
                pctx.fillStyle = COLORS[pixels[r][c]];
                pctx.fillRect(c * scale, r * scale, scale, scale);
              }
            }
          }
          wrap.appendChild(preview);
          return wrap;
        };

        item.appendChild(createFacePreview(pair.frontPixels || [], 'Front'));
        item.appendChild(createFacePreview(pair.backPixels || [], 'Back'));

        // Click to load
        item.addEventListener('click', () => this.loadSavedPair(idx));

        // Audio upload button
        const audioBtn = document.createElement('button');
        audioBtn.className = 'card-pixel-art-btn card-pixel-art-btn--audio-upload';
        audioBtn.dataset.audioIdx = idx;
        audioBtn.textContent = pair.audioUrl ? '⬆ Re-upload' : '⬆ Upload Audio';
        audioBtn.title = pair.audioUrl ? ('Uploaded: ' + (pair.audioName || 'Audio')) : 'Upload audio file';
        audioBtn.style.cssText = 'font-size:12px;padding:4px 8px;margin:4px 0;background-color:rgba(128,128,128,0.15);';
        audioBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          self.uploadAudioForPair(idx);
        });
        item.appendChild(audioBtn);

        // Delete button
        const delBtn = document.createElement('button');
        delBtn.className = 'card-pixel-art-editor__saved-delete';
        delBtn.textContent = '✕';
        delBtn.title = 'Delete';
        delBtn.addEventListener('click', (e) => {
          e.stopPropagation();
                    const msg = pair.audioUrl
                      ? 'Both images and the uploaded audio file will be deleted. Are you sure?'
                      : 'Both images will be deleted. Are you sure?';
                    if (confirm(msg)) self.deleteSavedPair(idx);
        });
        item.appendChild(delBtn);

        grid.appendChild(item);
      });
    }
  }

  // ─── CubePreview Class ──────────────────────────────

  const FACES = [
    { name: 'Front',  slot: 1, nx: 0, ny: 0, nz: 1  },
    { name: 'Right', slot: 4, nx: 1, ny: 0, nz: 0  },
    { name: 'Back',  slot: 3, nx: 0, ny: 0, nz: -1 },
    { name: 'Left', slot: 2, nx: -1, ny: 0, nz: 0  },
    { name: 'Top', slot: 5, nx: 0, ny: 1, nz: 0  }
  ];

  const COS30 = Math.cos(Math.PI / 6); // ≈0.866
  const SIN30 = 0.5;

  class CubePreview {
    constructor(editor) {
      this.editor = editor;
      this.angle = -0.35; // start showing front-right
      this.autoRotate = true; // rotate by default
      this.rotationSpeed = 0.008;
      this.hoveredFace = null;
      this.canvas = document.getElementById('card-pixel-art-cube-canvas');
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d');
      this.size = 900; // 2D canvas size (3x)
      this.canvas.width = this.size;
      this.canvas.height = this.size * 0.85;
      this.cx = this.canvas.width / 2;
      this.cy = this.canvas.height * 0.55;

      this.initEvents();
      this.animate();
    }

    // Rotate a 3D point around Y axis
    rotateY(x, y, z, angle) {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return {
        x: x * cos + z * sin,
        y: y,
        z: -x * sin + z * cos
      };
    }

    // Isometric project: 3D → 2D
    project(x, y, z) {
      return {
        sx: this.cx + (x - z) * COS30,
        sy: this.cy + (x + z) * SIN30 - y
      };
    }

    // Get projected 2D vertices of a cube face
    getFaceProjection(faceDef, half) {
      const { nx, ny, nz } = faceDef;
      // Build face vertices in local 3D
      let v3d = [];
      if (nx !== 0) {
        // Face perpendicular to X axis
        const sx = nx * half;
        v3d = [
          { x: sx, y: -half, z: -half },
          { x: sx, y: -half, z:  half },
          { x: sx, y:  half, z:  half },
          { x: sx, y:  half, z: -half }
        ];
      } else if (ny !== 0) {
        // Face perpendicular to Y axis (top/bottom)
        const sy = ny * half;
        v3d = [
          { x: -half, y: sy, z: -half },
          { x:  half, y: sy, z: -half },
          { x:  half, y: sy, z:  half },
          { x: -half, y: sy, z:  half }
        ];
      } else {
        // Face perpendicular to Z axis
        const sz = nz * half;
        v3d = [
          { x: -half, y: -half, z: sz },
          { x:  half, y: -half, z: sz },
          { x:  half, y:  half, z: sz },
          { x: -half, y:  half, z: sz }
        ];
      }

      // Rotate and project
      return v3d.map(v => {
        const r = this.rotateY(v.x, v.y, v.z, this.angle);
        return this.project(r.x, r.y, r.z);
      });
    }

    // Get Z depth for sorting (painter's algorithm)
    getFaceDepth(faceDef, half) {
      const center = this.rotateY(faceDef.nx * half, faceDef.ny * half, faceDef.nz * half, this.angle);
      return center.z;
    }

    // Create face texture from draft data with border
    createFaceTexture(slot) {
      const drafts = this.editor.getDrafts();
      const draft = drafts.find(d => d.slot === slot);
      // Support both shell editor (canvasSize) and card editor (canvasCols)
      const canvasSize = (draft ? (draft.canvasCols || draft.canvasSize) : (this.editor.canvasCols || this.editor.canvasSize)) || 45;
      const borderW = canvasSize === 16 ? 2 : 4;
      const texSize = canvasSize + borderW * 2;
      const offCanvas = document.createElement('canvas');
      offCanvas.width = texSize;
      offCanvas.height = texSize;
      const ctx = offCanvas.getContext('2d');

      // Fill border with selected border color
      ctx.fillStyle = COLORS[this.editor.borderColor] || '#000';
      ctx.fillRect(0, 0, texSize, texSize);

      // Draw pixel art in interior (vertically flipped for 3D projection)
      const pixelData = draft ? draft.pixels : null;
      for (let r = 0; r < canvasSize; r++) {
        const flippedR = canvasSize - 1 - r;
        for (let c = 0; c < canvasSize; c++) {
          const colorIdx = (pixelData && pixelData[r] && pixelData[r][c] != null) ? pixelData[r][c] : null;
          if (colorIdx !== null) {
            ctx.fillStyle = COLORS[colorIdx];
            ctx.fillRect(borderW + c, borderW + flippedR, 1, 1);
          }
        }
      }

      // Restricted zone overlay for top face (also flipped)
      if (slot === 5) {
        const spanH = Math.floor(canvasSize / 8);
        const spanW = Math.floor(canvasSize * 3 / 4);
        const startR = Math.floor((canvasSize - spanH) / 2);
        const startC = Math.floor((canvasSize - spanW) / 2);
        const flippedStartR = canvasSize - startR - spanH;
        ctx.fillStyle = '#000000';
        ctx.fillRect(borderW + startC, borderW + flippedStartR, spanW, spanH);
      }

      return offCanvas;
    }

    // Draw a single face using affine transform
    drawFace(v2d, texture) {
      const ctx = this.ctx;
      const texW = texture.width;
      const texH = texture.height;

      // Compute affine transform that maps texture (0,0)-(W,H) to face v2d
      // v2d: [topLeft, topRight, bottomRight, bottomLeft]
      const dx = v2d[1].sx - v2d[0].sx;
      const dy = v2d[1].sy - v2d[0].sy;
      const dxx = v2d[3].sx - v2d[0].sx;
      const dyy = v2d[3].sy - v2d[0].sy;
      const ox = v2d[0].sx;
      const oy = v2d[0].sy;

      if (Math.abs(dx * dyy - dy * dxx) < 0.001) return; // degenerate

      const a = dx / texW;
      const b = dy / texW;
      const c = dxx / texH;
      const d = dyy / texH;

      ctx.save();
      ctx.imageSmoothingEnabled = false;
      ctx.setTransform(a, b, c, d, ox, oy);
      ctx.drawImage(texture, 0, 0);
      ctx.restore();
    }

    // Check if mouse is inside a convex quadrilateral
    pointInQuad(px, py, quad) {
      let inside = true;
      for (let i = 0; i < 4; i++) {
        const j = (i + 1) % 4;
        const ex = quad[j].sx - quad[i].sx;
        const ey = quad[j].sy - quad[i].sy;
        const cross = ex * (py - quad[i].sy) - ey * (px - quad[i].sx);
        if (cross < 0) { inside = false; break; }
      }
      return inside;
    }

    render() {
      const ctx = this.ctx;
      const half = 180; // half cube edge in 3D units (3x)
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      // Build face data with depth
      const faceData = FACES.map(f => ({
        ...f,
        quad: this.getFaceProjection(f, half),
        depth: this.getFaceDepth(f, half),
        texture: this.createFaceTexture(f.slot)
      }));

      // Separate side faces from top face — top is always drawn last (we view from above)
      const sideFaces = faceData.filter(f => f.nx !== 0 || f.ny !== 1 || f.nz !== 0);
      const topFace = faceData.find(f => f.nx === 0 && f.ny === 1 && f.nz === 0);

      // Sort side faces by depth (far to near) — painter's algorithm
      sideFaces.sort((a, b) => a.depth - b.depth);

      // Draw faces: sides first, top last
      const drawOrder = [...sideFaces, ...(topFace ? [topFace] : [])];

      for (const face of drawOrder) {
        // Draw face fill (dark bg)
        ctx.fillStyle = face.name === 'Top' ? '#3a3a3a' : '#2a2a2a';
        ctx.beginPath();
        ctx.moveTo(face.quad[0].sx, face.quad[0].sy);
        for (let i = 1; i < 4; i++) {
          ctx.lineTo(face.quad[i].sx, face.quad[i].sy);
        }
        ctx.closePath();
        ctx.fill();

        // Draw textured face
        this.drawFace(face.quad, face.texture);

        // Highlight hovered face
        if (face.name === this.hoveredFace) {
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(face.quad[0].sx, face.quad[0].sy);
          for (let i = 1; i < 4; i++) ctx.lineTo(face.quad[i].sx, face.quad[i].sy);
          ctx.closePath();
          ctx.stroke();

          // Show face name label
          const cx = face.quad.reduce((s, v) => s + v.sx, 0) / 4;
          const cy = face.quad.reduce((s, v) => s + v.sy, 0) / 4;
          ctx.font = 'bold 13px -apple-system, sans-serif';
          const label = face.name;
          const metrics = ctx.measureText(label);
          ctx.fillStyle = 'rgba(0,0,0,0.75)';
          ctx.fillRect(cx - metrics.width / 2 - 6, cy - 10, metrics.width + 12, 22);
          ctx.fillStyle = '#fff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, cx, cy + 1);
        }
      }
    }

    initEvents() {
      let isDragging = false;
      let dragStartX = 0;
      let dragStartAngle = 0;
      let hasDragged = false;
      let resumeTimer = null;

      const startDrag = (clientX) => {
        isDragging = true;
        hasDragged = false;
        dragStartX = clientX;
        dragStartAngle = this.angle;
        this.autoRotate = false;
        if (resumeTimer) clearTimeout(resumeTimer);
        this.canvas.style.cursor = 'grabbing';
      };

      const moveDrag = (clientX) => {
        if (!isDragging) return;
        const dx = clientX - dragStartX;
        if (Math.abs(dx) > 3) hasDragged = true;
        this.angle = dragStartAngle + dx * 0.008;
      };

      const endDrag = () => {
        if (!isDragging) return;
        isDragging = false;
        this.canvas.style.cursor = 'grab';
        // Resume auto-rotate after 3 seconds of inactivity
        if (resumeTimer) clearTimeout(resumeTimer);
        resumeTimer = setTimeout(() => {
          this.autoRotate = true;
        }, 3000);
      };

      // Mouse events
      this.canvas.addEventListener('mousedown', (e) => {
        e.preventDefault();
        startDrag(e.clientX);
      });
      document.addEventListener('mousemove', (e) => {
        moveDrag(e.clientX);
      });
      document.addEventListener('mouseup', () => {
        endDrag();
      });

      // Touch events
      this.canvas.addEventListener('touchstart', (e) => {
        e.preventDefault();
        startDrag(e.touches[0].clientX);
      }, { passive: false });
      this.canvas.addEventListener('touchmove', (e) => {
        e.preventDefault();
        moveDrag(e.touches[0].clientX);
      }, { passive: false });
      this.canvas.addEventListener('touchend', () => {
        endDrag();
      });

      this.canvas.style.cursor = 'grab';
    }

    animate() {
      if (this.autoRotate) {
        this.angle += this.rotationSpeed;
      }
      this.render();
      requestAnimationFrame(() => this.animate());
    }
  }

  // ─── Initialize (window-based singleton for cross-closure safety) ────
  function initEditor() {
    console.log('[Card] initEditor called, found editor:', !!document.querySelector('.card-pixel-art-editor'));
    if (!document.querySelector('.card-pixel-art-editor')) return;

    if (window.__cardPixelArtEditor) {
      try {
        if (window.__cardPixelArtEditor.autoSaveTimer) clearInterval(window.__cardPixelArtEditor.autoSaveTimer);
      } catch(e) {}
      window.__cardPixelArtEditor = null;
    }

    window.__cardPixelArtEditor = new PixelArtEditor();
    console.log('[Card] Editor created, singleton set:', !!window.__cardPixelArtEditor);
  }

  // Normal page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEditor);
  } else {
    setTimeout(initEditor, 0);
  }

  // Shopify theme editor: re-init when section is loaded/selected
  document.addEventListener('shopify:section:load', function(e) {
    if (e.target && e.target.querySelector('.card-pixel-art-editor')) {
      initEditor();
    }
  });

  // ─── Document-level event delegation (only register ONCE across all closures) ──
  if (!window.__cardPixelArtDelegationBound) {
    window.__cardPixelArtDelegationBound = true;

    document.addEventListener('click', function(e) {
      var editor = window.__cardPixelArtEditor;
      if (!editor) return;
      var target = e.target.closest('#card-pixel-art-undo, #card-pixel-art-clear, #card-pixel-art-save, #card-pixel-art-upload, #card-pixel-art-eraser, #card-pixel-art-eyedropper, #card-pixel-art-rotate, #card-pixel-art-mirror, #card-pixel-art-delete, #card-pixel-art-face-front, #card-pixel-art-face-back, #card-pixel-art-save-pair, #card-pixel-art-refloat, .pixel-auth-save-btn');
      if (!target) return;
      console.log('[Card] Button clicked:', target.id || target.className);
      if (target.id === 'card-pixel-art-undo') editor.undo();
      if (target.id === 'card-pixel-art-clear') editor.clear();
      if (target.id === 'card-pixel-art-rotate') editor.rotateFloatingImage();
      if (target.id === 'card-pixel-art-mirror') editor.mirrorFloatingImage();
      if (target.id === 'card-pixel-art-delete') editor.deleteFloatingImage();
      if (target.id === 'card-pixel-art-refloat') {
        if (editor.floatingImages.length > 0) editor.commitFloatingImages();
        else editor.refloatCanvas();
      }
      if (target.id === 'card-pixel-art-eraser') editor.toggleEraser();
      if (target.id === 'card-pixel-art-eyedropper') editor.toggleEyedropper();
      if (target.id === 'card-pixel-art-face-front') editor.switchFace(0);
      if (target.id === 'card-pixel-art-face-back') editor.switchFace(1);
      if (target.id === 'card-pixel-art-save-pair') editor.savePair();
      if (target.id === 'card-pixel-art-save' || target.id === 'card-pixel-art-upload' || target.classList.contains('pixel-auth-save-btn')) editor.saveDraft();
    });

    // Click outside canvas while in move mode → commit floating images
    document.addEventListener('mousedown', function(e) {
      var editor = window.__cardPixelArtEditor;
      if (!editor || editor.floatingImages.length === 0) return;
      // Skip clicks on floating-image-related buttons
      if (e.target.closest('#card-pixel-art-rotate, #card-pixel-art-mirror, #card-pixel-art-delete, #card-pixel-art-refloat')) return;
      // Skip if click is on either canvas (canvas handles its own commit logic)
      if (editor.canvas && editor.canvas.contains(e.target)) return;
      if (editor.frontCanvasEl && editor.frontCanvasEl.contains(e.target)) return;
      if (editor.backCanvasEl && editor.backCanvasEl.contains(e.target)) return;
      // Click is outside both canvases → commit
      editor.commitFloatingImages();
    });

    document.addEventListener('keydown', function(e) {
      if (!editor) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        editor.undo();
      }
    });
  }
})();
